import { DisbursementDataType, PaymentKey } from "pages/Payment/config";
import {
  CreateDocumentResponse,
  DeleteDocumentRequest,
} from "redux/services/documentRepository/types";
import env from "../../env";

/**
 * Get the file upload polling interval from environment or use default
 * Environment variable: REACT_APP_FILE_UPLOAD_POLLING_INTERVAL
 * Default: 5000ms (5 seconds)
 */
export const getFileUploadPollingInterval = (): number => {
  return Number(env.polling?.fileUploadInterval) || 5000;
};

export function resetRequestDetailsInterval(
  intervalRef: { current: any },
  refreshRequestDetails: () => void | Promise<any>
) {
  if (intervalRef.current) {
    clearInterval(intervalRef.current);
  }
  intervalRef.current = setInterval(() => {
    refreshRequestDetails();
  }, getFileUploadPollingInterval());
}

// New dual polling function for data manager + validation results
export function resetDualPollingInterval(
  intervalRef: { current: any },
  refreshRequestDetails: () => void | Promise<any>,
  refreshValidationResults: () => void | Promise<any>
) {
  if (intervalRef.current) {
    clearInterval(intervalRef.current);
  }
  intervalRef.current = setInterval(() => {
    // Poll both services according to spec
    refreshRequestDetails();
    refreshValidationResults();
  }, getFileUploadPollingInterval());
}

export function resetRequestDocumentInterval(
  intervalRef: React.MutableRefObject<any>,
  getDocument: (args: {
    fetchMetaDataOnly: boolean;
    documentId: string;
    orgToken?: string;
  }) => void,
  uploadFileData: any,
  orgToken?: string
) {
  if (intervalRef.current) {
    clearInterval(intervalRef.current);
  }

  intervalRef.current = setInterval(() => {
    const id = uploadFileData?.data?.id;
    if (id) {
      getDocument({
        fetchMetaDataOnly: true,
        documentId: id,
        orgToken,
      });
    }
  }, getFileUploadPollingInterval());
}

export function resetGetDocumentPrefundInterval(
  intervalRef: { current: any },
  getPrefundDocument: (opts: {
    fetchMetaDataOnly: boolean;
    documentId: string;
    orgToken?: string;
  }) => void,
  uploadProofFileData?: { data?: { id: string } },
  orgToken?: string
) {
  if (intervalRef.current) {
    clearInterval(intervalRef.current);
  }
  intervalRef.current = setInterval(() => {
    if (uploadProofFileData?.data?.id) {
      getPrefundDocument({
        fetchMetaDataOnly: true,
        documentId: uploadProofFileData.data.id,
        orgToken,
      });
    }
  }, getFileUploadPollingInterval());
}

export function handleRemoveFile({
  key,
  intervalRef,
  uploadFileData,
  deleteDocument,
  resetFns,
  setDisbursementData,
  disbursementData,
  setPresignPayrollState,
  setCanReloadRequestDetails,
  orgToken,
}: {
  key: PaymentKey;
  intervalRef: { current: any };
  uploadFileData: CreateDocumentResponse | undefined;
  deleteDocument: (params: DeleteDocumentRequest) => void;
  resetFns: {
    resetDocumentDetail?: () => void;
    resetUploadFile: () => void;
    resetUploadPayrollFile?: () => void;
  };
  setDisbursementData: (data: DisbursementDataType[]) => void;
  disbursementData: DisbursementDataType[];
  setPresignPayrollState?: (state: any) => void;
  setCanReloadRequestDetails?: (flag: boolean) => void;
  orgToken?: string;
}) {
  // reset the UI checklist elements
  const newData = disbursementData.map((item) => {
    if (item.key === key) {
      return { ...item, isSuccess: false, files: [] };
    }
    return item;
  });
  setDisbursementData(newData);

  if (intervalRef.current) {
    clearTimeout(intervalRef.current);
  }

  if (key === PaymentKey.UPLOAD_PAYROLL_FILE && uploadFileData?.data?.id) {
    deleteDocument({
      documentId: uploadFileData.data.id,
      orgToken,
    });
  }

  if (key === PaymentKey.UPLOAD_PAYROLL_FILE) {
    resetFns.resetDocumentDetail?.();
    resetFns.resetUploadFile();
    resetFns.resetUploadPayrollFile?.();
    setPresignPayrollState?.({});
    setCanReloadRequestDetails?.(false);
  }
}

export function setNewDisbursementData(
  prevData: DisbursementDataType[],
  hookDisbursementData: DisbursementDataType[]
) {
  const updatedData = prevData.map((item) => {
    const newItem = hookDisbursementData.find(
      (newItem) => newItem.key === item.key
    );
    return newItem ? { ...item, ...newItem } : item;
  });
  return updatedData;
}
